import { useState } from 'react'
import { UserViewModel } from '@ail/customers-api'
import Modal from '../../../common/Modal/Modal'
import IdIsCopied from '../../../../helpers/IdIsCopied'

interface IAllowedUsersListProps {
  users: UserViewModel[]
  allowedUsers: string[]
  filteredUsersByOwner: UserViewModel[]
}

export default function AllowedUsersList({ allowedUsers, users, filteredUsersByOwner }: IAllowedUsersListProps) {
  const [inputValue, setInputValue] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [idCopied, setIdCopied] = useState(false)
  const [emailCopied, setEmailCopied] = useState(false)

  const [modalInputValue, setModalInputValue] = useState('')

  const validUsers = allowedUsers?.length
    ? filteredUsersByOwner.filter((u) => allowedUsers.includes(u.id))
    : filteredUsersByOwner

  const userIds = new Set(filteredUsersByOwner.map((u) => u.id))
  const wrongAllowedUsers = allowedUsers?.filter((id) => id !== '' && !userIds.has(id))

  const usersToMap = filterUsers(validUsers, inputValue)
  const notSelectedUsers = allowedUsers?.length ? users.filter((u) => !allowedUsers.includes(u.id)) : []
  const modalUsersToMap = filterUsers(notSelectedUsers, modalInputValue)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, modal?: boolean) => {
    if (modal) {
      setModalInputValue(event.target.value)
    } else setInputValue(event.target.value)
  }

  const copyId = async (value: string, email?: boolean) => {
    try {
      await navigator.clipboard.writeText(value)
      if (email) {
        setEmailCopied(true)
        setIdCopied(false)
      } else {
        setEmailCopied(false)
        setIdCopied(true)
      }

      setTimeout(() => {
        setEmailCopied(false)
        setIdCopied(false)
      }, 5000)
    } catch {
      setIdCopied(false)
      setEmailCopied(false)
    }
  }

  return (
    <>
      <div className="allowed-users">
        <div className="detailTitle docTitleArea relative">
          <div className="titleDoc">
            <span>
              <i className="icon-user" />
              Allowed users
              <button onClick={() => setModalOpen(true)} className="button">
                <i className="icon icon-add" />
              </button>
            </span>
            <div className="toolArea">
              <div className="input-area">
                <input
                  placeholder="Search user"
                  value={inputValue}
                  onChange={handleInputChange}
                  className="transparent"
                />
                {inputValue && <i className="icon-clear" onClick={() => setInputValue('')} />}
                <i className="icon-search" />
              </div>
            </div>
          </div>
        </div>
        <div className="ordersContent">
          <ul>
            {usersToMap.length > 0 ? (
              usersToMap.map((user) => (
                <li
                  className={`user ${
                    !user.enabled ? 'disabled' : user.permissions.includes('reports.view') ? 'check' : 'warning'
                  }`}
                  key={user.id}
                >
                  <div>
                    <i className="icon icon-user" />
                    <span>
                      {user.firstName} {user.lastName} | {user.name}
                    </span>
                  </div>
                  <div className="iconsArea">
                    <i className="icon-mail" onClick={() => void copyId(user.name, true)} />
                    <i className="icon-id" onClick={() => void copyId(user.id)} />
                  </div>
                </li>
              ))
            ) : (
              <li className="user notFound">
                <div>
                  <i className="icon-user" />
                  <span>
                    {allowedUsers.length
                      ? `User "${inputValue || allowedUsers[0]}" not found`
                      : 'No matching users found'}
                  </span>
                </div>
              </li>
            )}
            {usersToMap.length > 0 &&
              wrongAllowedUsers.map((u) => (
                <li className="user notFound" key={u}>
                  <div>
                    <i className="icon-user" />
                    User "{u}" not found
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>
      <Modal
        visible={modalOpen}
        icon={'icon-user'}
        children={
          <>
            <div className="customer-name-filter-ui">
              <div className="search input-area">
                <input
                  placeholder="Search user to add"
                  value={modalInputValue}
                  onChange={(e) => handleInputChange(e, true)}
                />
                {modalInputValue && <i className="icon-clear" onClick={() => setModalInputValue('')} />}
                <i className="icon-search" />
              </div>
            </div>
            <div className="ordersContent">
              <ul>
                {modalUsersToMap.length > 0 ? (
                  modalUsersToMap.map((user) => (
                    <li
                      className={`user ${user.permissions.includes('reports.view') ? 'check' : 'warning'}`}
                      key={user.id}
                    >
                      <div>
                        <i className="icon icon-user" />
                        {user.firstName} {user.lastName} | {user.name}
                      </div>
                      <div className="iconsArea">
                        <i className="icon-mail" onClick={() => void copyId(user.name)} />
                        <i className="icon-id" onClick={() => void copyId(user.id)} />
                      </div>
                    </li>
                  ))
                ) : modalInputValue ? (
                  <li className="notFound">User "{modalInputValue}" was not found.</li>
                ) : (
                  <li className="notFound">All users are selected.</li>
                )}
              </ul>
            </div>
          </>
        }
        onClose={() => setModalOpen(false)}
        title={'Add new allowed user'}
      />
      {(idCopied || emailCopied) && <IdIsCopied idCopied={idCopied || emailCopied} value={emailCopied} />}
    </>
  )
}

function filterUsers(users: UserViewModel[], value = '') {
  if (value.length < 2) return users

  const _value = value.toLowerCase()

  const filteredUsers = users.filter((u) => {
    const nameMatches = u.firstName?.toLowerCase().includes(_value) || u.lastName?.toLowerCase().includes(_value)
    const idMatches = u.id.toLowerCase().includes(_value)
    const emailMatches = u.name?.toLowerCase().includes(_value)
    return nameMatches || idMatches || emailMatches
  })
  return filteredUsers
}
